import axios from "axios";
import LocalStorage from "../shared/localStorage";
import { Auth } from "../routes/Constants";
import APIVariables from "./api-variables";

// Auth is cookie-based only; no Bearer token. Cookies sent via withCredentials.

const CSRF_HEADER_NAME = "X-CSRFToken";
const UNSAFE_HTTP_METHODS = new Set(["post", "put", "patch", "delete"]);

// Axios defaults are XSRF-TOKEN / X-XSRF-TOKEN (Laravel). Django uses csrftoken + X-CSRFToken.
// The XHR adapter reads csrftoken and sets X-CSRFToken when the cookie is visible to JS.
const instance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  withCredentials: true,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: CSRF_HEADER_NAME,
});

// Local dev only: UI on localhost + API on another host → document.cookie cannot read
// csrftoken; the browser still sends the cookie but Axios cannot mirror it into X-CSRFToken.
// REACT_APP_CSRF_DEV_TOKEN supplies the same value (see .env.development).
instance.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (!UNSAFE_HTTP_METHODS.has(method)) return config;

  const devToken = process.env.REACT_APP_CSRF_DEV_TOKEN;
  if (!devToken || !String(devToken).trim()) return config;

  const headers = config.headers;
  if (!headers) return config;
  const value = String(devToken).trim();
  if (typeof headers.set === "function") {
    headers.set(CSRF_HEADER_NAME, value);
  } else {
    headers[CSRF_HEADER_NAME] = value;
  }
  return config;
});

function getSessionExpiredMessage(error) {
  const data = error?.response?.data;
  return data?.tokenExpired ?? data?.detail ?? null;
}

function clearAuthAndRedirectToLogin(error) {
  localStorage.removeItem(LocalStorage.USER_NHSPORTAL);
  const message = getSessionExpiredMessage(error);
  if (message != null) {
    const value =
      typeof message === "string" ? message : JSON.stringify(message);
    localStorage.setItem(LocalStorage.EXPIRED_SESSION_MESSAGE_NHSPORTAL, value);
  }
  window.location.href = Auth.LOGIN;
}

function isLoginRequest(config) {
  const url = config?.url ?? "";
  const method = config?.method?.toLowerCase();
  return url.includes(APIVariables.USERS_LOGIN) && method === "post";
}

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 && !isLoginRequest(error?.config)) {
      clearAuthAndRedirectToLogin(error);
    }
    return Promise.reject(error);
  }
);

export default instance;
